import {
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  HelpCircle,
  XCircle,
} from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useUpdateIntakeMutation } from '@/services/api/intakeService';
import { ManualQualificationEnum } from '@/services/types/intake-types';

/**
 * ManualQualificationControl
 *
 * A collapsible panel for manually marking prospects as qualified or unqualified.
 * Follows the MetadataPanel design pattern with subtle, professional styling.
 *
 * Behavior:
 * - Collapses/expands like MetadataPanel
 * - Displays current qualification status with subtle visual treatment
 * - Provides action buttons for marking qualified/unqualified
 * - Uses optimistic updates for instant feedback
 * - Graceful error handling with rollback
 */

interface ManualQualificationControlProps {
  /** The intake ID */
  intakeId?: string | null;
  /** Current manual qualification value */
  value?: ManualQualificationEnum | null;
  /** Rejection reason if the intake was rejected */
  rejectionReason?: string | null;
  /** Whether rejection email has been sent */
  rejectionEmailSent?: boolean;
  /** Optional className for the container */
  className?: string;
  /** Disable editing (read-only mode) */
  disabled?: boolean;
  /** Callback to refetch data after successful update */
  onUpdateSuccess?: () => void;
}

export const ManualQualificationControl = ({
  intakeId,
  value,
  rejectionReason,
  rejectionEmailSent = false,
  className,
  disabled = false,
  onUpdateSuccess,
}: ManualQualificationControlProps) => {
  const [updateIntake, { isLoading }] = useUpdateIntakeMutation();
  const [isExpanded, setIsExpanded] = useState(true);
  const [isUpdatingEmailSent, setIsUpdatingEmailSent] = useState(false);

  const handleQualificationChange = async (
    qualification: ManualQualificationEnum | null,
  ) => {
    if (!intakeId) {
      toast.error('Cannot update: intake ID is missing');
      return;
    }

    try {
      await updateIntake({
        id: intakeId,
        manual_qualification: qualification,
      }).unwrap();

      const qualificationLabels: Record<ManualQualificationEnum, string> = {
        [ManualQualificationEnum.QUALIFIED]: 'Qualified',
        [ManualQualificationEnum.UNQUALIFIED]: 'Unqualified',
        [ManualQualificationEnum.UNKNOWN]: 'Unknown',
      };
      const message = qualification
        ? `Marked as ${qualificationLabels[qualification]}`
        : 'Qualification cleared';
      toast.success(message);

      // Refetch data to ensure UI shows latest state
      onUpdateSuccess?.();
    } catch (error) {
      console.error('Failed to update qualification:', error);
      toast.error('Failed to update qualification status');
    }
  };

  const handleRejectionEmailSentChange = async (checked: boolean) => {
    if (!intakeId) {
      toast.error('Cannot update: intake ID is missing');
      return;
    }

    setIsUpdatingEmailSent(true);
    try {
      await updateIntake({
        id: intakeId,
        rejection_email_sent: checked,
      }).unwrap();

      toast.success(
        checked
          ? 'Marked as rejection email sent'
          : 'Unmarked rejection email sent',
      );
      // Note: Don't call onUpdateSuccess() here - RTK Query's cache invalidation
      // handles the refresh. Calling refetch() would trigger auto-save which
      // could overwrite this change.
    } catch (error) {
      console.error('Failed to update rejection email sent:', error);
      toast.error('Failed to update rejection email sent status');
    } finally {
      setIsUpdatingEmailSent(false);
    }
  };

  const isQualified = value === ManualQualificationEnum.QUALIFIED;
  const isUnqualified = value === ManualQualificationEnum.UNQUALIFIED;
  const isUnknown = value === ManualQualificationEnum.UNKNOWN;

  return (
    <div
      className={cn(
        'bg-white border border-gray-200 rounded-lg shadow-sm',
        className,
      )}
    >
      {/* Header */}
      <Button
        variant="ghost"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full justify-between p-3 text-left hover:bg-gray-50"
        aria-expanded={isExpanded}
      >
        <div className="flex items-center gap-2">
          {isQualified && <CheckCircle2 className="h-4 w-4 text-green-600" />}
          {isUnqualified && <XCircle className="h-4 w-4 text-red-500" />}
          {isUnknown && <HelpCircle className="h-4 w-4 text-amber-500" />}
          {!value && (
            <div className="h-4 w-4 rounded-full border-2 border-gray-300" />
          )}
          <span className="text-sm font-medium text-gray-700">
            Case Qualification
          </span>
        </div>
        {isExpanded ? (
          <ChevronDown className="h-4 w-4 text-gray-500" />
        ) : (
          <ChevronRight className="h-4 w-4 text-gray-500" />
        )}
      </Button>

      {/* Content */}
      {isExpanded && (
        <div className="px-2 pb-3 border-t border-gray-100">
          <div className="space-y-3 pt-3">
            {/* Current Status Display */}
            {value && (
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-600 font-medium">Status:</span>
                {isQualified && (
                  <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md font-medium bg-green-50 text-green-700 border border-green-200">
                    <CheckCircle2 className="h-3 w-3" />
                    Qualified
                  </span>
                )}
                {isUnqualified && (
                  <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md font-medium bg-red-50 text-red-700 border border-red-200">
                    <XCircle className="h-3 w-3" />
                    Unqualified
                  </span>
                )}
                {isUnknown && (
                  <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md font-medium bg-amber-50 text-amber-700 border border-amber-200">
                    <HelpCircle className="h-3 w-3" />
                    Unknown
                  </span>
                )}
              </div>
            )}

            {/* Rejection Reason Display */}
            {rejectionReason && (
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-600 font-medium">
                  Rejection Reason:
                </span>
                <span className="inline-flex items-center px-2 py-1 rounded-md font-medium bg-red-50 text-red-700 border border-red-200">
                  {rejectionReason}
                </span>
              </div>
            )}

            {/* Rejection Email Sent Checkbox */}
            <div className="flex items-center justify-between text-xs">
              <span
                className="text-gray-600 font-medium"
                id="rejection-email-label"
              >
                Rejection Email Sent:
              </span>
              <div className="inline-flex items-center">
                <input
                  id="rejection-email-sent-checkbox"
                  type="checkbox"
                  checked={rejectionEmailSent}
                  onChange={(e) =>
                    handleRejectionEmailSentChange(e.target.checked)
                  }
                  disabled={isUpdatingEmailSent || disabled || !intakeId}
                  className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                  aria-labelledby="rejection-email-label"
                />
                <label
                  htmlFor="rejection-email-sent-checkbox"
                  className="ml-2 text-gray-700 cursor-pointer"
                >
                  {rejectionEmailSent ? 'Yes' : 'No'}
                </label>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-2">
              {!value && (
                <p className="text-xs text-gray-500 mb-2">
                  Mark this case as qualified or unqualified:
                </p>
              )}

              {!value && (
                <div className="grid grid-cols-3 gap-1">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      handleQualificationChange(
                        ManualQualificationEnum.QUALIFIED,
                      )
                    }
                    disabled={isLoading || disabled || !intakeId}
                    className={cn(
                      'h-8 text-xs font-medium',
                      'border-green-200 hover:bg-green-600 hover:border-green-300 bg-green-700 text-white hover:text-white',
                    )}
                  >
                    <CheckCircle2 className="h-3.5 w-3.5" />
                    Qualified
                  </Button>

                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      handleQualificationChange(ManualQualificationEnum.UNKNOWN)
                    }
                    disabled={isLoading || disabled || !intakeId}
                    className={cn(
                      'h-8 text-xs font-medium',
                      'border-amber-200 hover:bg-amber-600 hover:border-amber-300 bg-amber-600 text-white hover:text-white',
                    )}
                  >
                    <HelpCircle className="h-3.5 w-3.5" />
                    Unknown
                  </Button>

                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      handleQualificationChange(
                        ManualQualificationEnum.UNQUALIFIED,
                      )
                    }
                    disabled={isLoading || disabled || !intakeId}
                    className={cn(
                      'h-8 text-xs font-medium',
                      'border-red-200 hover:bg-red-600 hover:border-red-300 bg-red-700 text-white hover:text-white',
                    )}
                  >
                    <XCircle className="h-3.5 w-3.5" />
                    Unqualified
                  </Button>
                </div>
              )}

              {/* Clear button */}
              {value && !disabled && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => handleQualificationChange(null)}
                  disabled={isLoading || !intakeId}
                  className="w-full h-7 text-xs text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                >
                  Reset Qualification
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
